import { Injectable } from '@angular/core';
import { Observable } from "rxjs";
import { map, publishLast, refCount, tap } from 'rxjs/operators';
import { LogManager } from "../../../common/log/services/log.manager";
import { LogCat } from "../../../common/log/services/log.manager.categories";
import { IBaseFilter, IComplexFilter, ILookupFilter, LogicFilter, OperatorFilter } from '../interfaces/Ifilter.lookup';
import { ILookupMeta } from '../interfaces/Imeta.lookup';
import { ILookupSort, SortOrder } from '../interfaces/Isort.lookup';
import { IDataTable, ILookupTable } from '../interfaces/Itable.lookup';
import { LookupDelete } from "../interfaces/lookup.delete";
import { IBaseLookup } from "../lookups.types/interfaces/Ibase.lookup";
import { LookupRequest } from '../request.lookup';
import { LookupResponse } from '../response.lookup';
import { LookupsProxy } from './lookups.proxy';

type LookupsObserveResonse =  Observable<LookupResponse<IDataTable>[]>;
type cachedLookupsRequests={data:LookupsObserveResonse;validUntil:Date};
/**
	* ***************************************************************
	* The LookupsService service is the point of contact for all
	* application components requiring lookup information.
	*
	* Written by: Dana Moshe
	* ***************************************************************
	*/
@Injectable({ providedIn: 'root' })
  export class LookupsService {

	//Max int in C# 32 bit.
	MAX_INT:number = 2147483647;

	public static readonly ExcludedLookupColumnKeys = ['active', 'lastUpdated', 'tableName', 'localLookuptableKey'];

	public lookupsMeta: {[lookupName: string]: ILookupMeta};

	public runningRequests :any = {};	
	public cachedRequests :any = {};

	// we decided to cache the multi requests for 1 second to improve performance of "togther" functionalities.
	// for these usecases the chrome reduces the parallel XHR requests to 3 and we dont get the caching benefit.
	// in the long run we may improve this functionality, but this is safest and easiest way to improve performance.
	private cachedMultiRequests = new Map<any,cachedLookupsRequests>();
	private readonly MILIS_TO_CACHE_MULTI_REQUESTS=1*1500;

	constructor(private lookupProxy:LookupsProxy) {
	}

	// special interface since the request length are easily too long and not relevant for multi column filters
	public getLookupByMultiColumnSearch(lookup_name:string, lookup_table:string, filter_columns:string[],
										sort_column:string, sort_order: SortOrder, filter_string: string,
										min_row:number, max_row:number,extra_filter:IBaseFilter){
		return this.lookupProxy.getLookupByMultiColumnSearch(lookup_name, lookup_table, filter_columns, sort_column,
			sort_order, filter_string,  min_row, max_row,extra_filter);
	}
	/**
		* Get simple lookup for one request.
		* Simple lookups – a lookup structure containing a single entity (single table).
		* @param lookupRequest lookup request.
		* @returns a promise for the retrieved lookup.
		*/
	public getLookup<T extends IBaseLookup>(lookupRequest:LookupRequest):Observable<LookupResponse<T>> {
		if (this.runningRequests[<any>lookupRequest]){
			LogManager.debug(LogCat.LookupsCategories.Infra,"used running requests caching for lookup request", lookupRequest);
			return this.runningRequests[<any>lookupRequest];
		}

		if (lookupRequest.cacheKey && this.cachedRequests[lookupRequest.cacheKey]){
			LogManager.debug(LogCat.LookupsCategories.Infra,"used internal caching for lookup request", lookupRequest);
			return this.cachedRequests[lookupRequest.cacheKey];
		}

		// Send as array, so return the first element in the array (and the only).
		let retVal:Observable<LookupResponse<T>> = this.lookupProxy.getLookups([lookupRequest]).pipe(
			map((lookups:ILookupTable<T>[][]) => {
				//this.runningRequests[<any>lookupRequest]=undefined;
				delete this.runningRequests[<any>lookupRequest];
				return new LookupResponse(lookups[0][0], lookupRequest);
			}
		),publishLast(),refCount(),);

		this.runningRequests[<any>lookupRequest] = retVal;

		if (lookupRequest.cacheKey) {
			this.cachedRequests[lookupRequest.cacheKey] = retVal;
		}

		return retVal;
	}

	/**
		* Get simple lookups for array of requests.
		* Simple lookups – a lookup structure containing a single entity (single table).
		* Return the response by the same order that sent.
		* Important: if it possible to use in this function, this is better that send multiple requests to getLookup function.
		* @param  lookupsRequests array of lookup request.
		* @returns a single promise for an array of retrieved lookups.
		*/
	public getLookups(lookupsRequests:LookupRequest[]):Observable<LookupResponse<IDataTable>[]> {
		const multiCacheKeys=lookupsRequests.map(curr=>curr.cacheKey);
		const useMulityCache=multiCacheKeys.findIndex(x=>!x)===-1;
		let multyCacheKey;
		if (useMulityCache){
			multyCacheKey=multiCacheKeys.sort().join(",");			
			if(this.cachedMultiRequests.has(multyCacheKey)){			
				let cachedObj= this.cachedMultiRequests.get(multyCacheKey);
				if (cachedObj.validUntil>=new Date()){
					LogManager.debug(LogCat.LookupsCategories.Infra,"used running requests caching for multi lookup request", lookupsRequests);
					//sort the response by the order of the requests. in case the data comes from the cache, it may not be in the same order.
					let retVal = cachedObj.data.map((fromCache:LookupResponse<IDataTable>[]) => {					
						return fromCache.sort((a, b) => lookupsRequests.findIndex(x => x.cacheKey === a.lookupRequest.cacheKey) - 
							lookupsRequests.findIndex(x => x.cacheKey === b.lookupRequest.cacheKey));
					});
					return retVal;
				}else{
					LogManager.debug(LogCat.LookupsCategories.Infra,"cache outdated. removed.", lookupsRequests);
					this.cachedMultiRequests.delete(multyCacheKey); 
				}
			}
		}

		let retVal:Observable<LookupResponse<IDataTable>[]> = this.lookupProxy.getLookups(lookupsRequests).pipe(
			map((lookups:ILookupTable<IDataTable>[][]) => {
				
				let response:LookupResponse<IDataTable>[] = [];

				let reqIndex = 0;

				for (let i = 0; i < lookups.length; i++) {
					for (let j = 0; j < lookups[i].length; j++) {
						response.push(new LookupResponse(lookups[i][j], lookupsRequests[reqIndex]));
						++reqIndex;
					}					
				}

				return response;
			}),publishLast(),refCount(),);

		if (useMulityCache){
			let cacheValidityDate=new Date(new Date().getTime()+this.MILIS_TO_CACHE_MULTI_REQUESTS);
			this.cachedMultiRequests.set(multyCacheKey,{data:retVal,validUntil:cacheValidityDate}); 
		}		
		lookupsRequests.forEach((lookupRequest:LookupRequest, idx:number) => {
			if (lookupRequest.cacheKey) {
				this.cachedRequests[lookupRequest.cacheKey] =
					retVal.pipe(map((response:LookupResponse<IDataTable>[]) => response[idx]));
			}
		});

		return retVal;
	}

	/**
		* Get LookupMeta by filter.
		* @param filter string that used to filter the list of lookups names (case insensitive).
		* @returns a single promise for an array of retrieved lookup table meta-data.
		*/
	public listLookups(filter:string = ''):Observable<ILookupMeta[]> {
		return this.lookupProxy.listLookups(filter).pipe(
			tap(lookupsMeta => this.lookupsMeta = filter ? this.lookupsMeta : this.lookupsMetaToDictionary(lookupsMeta)));
	}

	protected lookupsMetaToDictionary(lookupsMeta: ILookupMeta[]) {
		return lookupsMeta.reduce((dict, currMeta) => Object.assign(dict, {
			[currMeta.lookupName]: currMeta
		}), {});
	}

	/**
		* The function return request that return all the record in the lookup (after filter).
		* @param lookupName  lookup name.
		* @param sort array that contain columns sort by priority.
		* @param filter the filter that run on the current lookup.
		* @returns lookup request.
		*/
	public createRequestGetAllRecords(lookupName:string, sort?:ILookupSort[], filter?:ILookupFilter,tableName?:string):LookupRequest {
		return new LookupRequest(lookupName, 0, this.MAX_INT, sort, filter,true,tableName?tableName.toUpperCase():tableName);
	}

	public clearCache(lookupRequest:LookupRequest) {
		if (lookupRequest.cacheKey && this.cachedRequests[lookupRequest.cacheKey]){
			LogManager.debug(LogCat.LookupsCategories.Infra,"clear cache for lookup request", lookupRequest);
			delete this.cachedRequests[lookupRequest.cacheKey];
		}
	}

	public insert(lookupName:string, data:any) : Observable<void> {
		return this.lookupProxy.insert(lookupName, data);
	}

	public update(lookupName:string, data:any) : Observable<void> {
		return this.lookupProxy.update(lookupName, data);
	}

	public delete(request:LookupDelete) : Observable<void> {
		return this.lookupProxy.delete(request);
	}

	/**
		* The function helps to create IBaseFilter object.
		* @param field name of column to filter.
		* @param operator the operator to use to filter.
		* @param value the value to compare to him.
		* @param isCaseInvariant string case invariant (if this value is true this is not matter lowercase or uppercase).
		* @returns base filter.
		*/
	public createBaseFilter(field:string, operator:OperatorFilter, value:any, isCaseInvariant:boolean):IBaseFilter {
		return {
			operator: operator,
			field: field,
			value: value,
			isCaseInvariant: isCaseInvariant
		};
	}

	/**
		* The function helps to create IComplexFilter object.
		* @param logic  the logic between the filter (and | or).
		* @param filters array of filters.
		* @returns complex filter.
		*/
	public createComplexFilter(logic:LogicFilter, filters:ILookupFilter[]):IComplexFilter {
		return {
			logic: logic,
			filters: filters
		};
	}

	/**
		* The function helps to create ILookupSort object.
		* @param field column name to sort by him.
		* @param dir how to sort the column (asc or desc).
		* @returns sort.
		*/
	public createSort(field:string, dir:SortOrder):ILookupSort {
		return {
			field: field,
			dir: dir
		};
	}

	/**
		* Get complex lookup.
		* Complex lookups – a lookup structure containing multiple entities (multiple tables).
		* @param lookupName name of dataSet.
		* @returns a promise for the retrieved lookup.
		*/
	public getComplexLookup<T extends IBaseLookup>(lookupName:string, useCache: boolean = true,filterActive=false):Observable<T> {
		if (this.cachedRequests[lookupName] && useCache){
			LogManager.debug(LogCat.LookupsCategories.Infra,"used internal caching for complex lookup request", lookupName);
			return this.cachedRequests[lookupName];
		}

		let retVal:Observable<T> = this.lookupProxy.getComplexLookups([{lookupName,includeClob:false}],filterActive).pipe(
			map((lookup:any[]) => {
				return lookup[0];
			}),publishLast(),refCount(),);

		this.cachedRequests[lookupName] = retVal;

		return retVal;
	}

	/**
		* Get complex lookups.
		* Complex lookups – a lookup structure containing multiple entities (multiple tables).
		* @param lookupName names of dataSet.
	 	*  @param filterActive notice the filter active is a single parameter for all lookups
		* @returns a single promise for an array of retrieved lookups.
		*/
	public getComplexLookups(lookupsNames:({lookupName:string;includeClob:boolean}|string)[],filterActive=false):Observable<IBaseLookup[]> {
		let req= lookupsNames.map(x=>typeof x === 'object'?<{lookupName:string;includeClob:boolean}>x:{lookupName:<string>x,includeClob:false});
		return this.lookupProxy.getComplexLookups(req,filterActive);
	}
}
